import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useClockPreview, useCurrentTime } from '@tradeoutlet/data';
import { ClipboardSignature, Coffee, LogIn, LogOut, MapPin, MapPinOff, Timer } from 'lucide-react';
import { CLOCK_ACTION_OP, applyPendingClock, entryMinutes, optionalId, pickClockLocation, type ClockAction, type GeoReading } from '@tradeoutlet/shared';
import { GEO_FAILURE_TEXT, readPosition, readPositionIfAllowed, type GeoFailure } from '@/lib/geo';
import { GeoBadge } from './clock/GeoBadge';
import { api, ApiError, errorMessage } from '@/lib/api';
import { dt, minutes, clock } from '@/lib/format';
import { useWorkspaceId } from '@/lib/hooks';
import { useUi } from '@/lib/store';
import { pendingClockIn, submitClock, usePendingClock } from '@/lib/clockOutbox';
import { WaitingMarker } from '@/components/clock/WaitingMarker';
import { Button, Card, Spinner } from '@/components/ui';
import { shiftTimes, useWfLookups } from './common';
import { HandoverFormModal, PendingHandoversModal, useHandoverTemplatesAvailable } from './handovers/HandoversUi';

/**
 * Clock in/out with breaks. client_event_id makes a retried clock-in idempotent.
 * Backlog #26: when the building has a geofence, the browser is asked for one location reading at clock-in / out
 * (never in the background). Enforced fences refuse an off-site clock-in; clock-out is never blocked.
 */
export function ClockWidget() {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const { locationName } = useWfLookups();
  const [now, setNow] = useState(Date.now());
  const [locationId, setLocationId] = useState('');
  const [showPending, setShowPending] = useState(false);
  const [writing, setWriting] = useState(false);
  const [geoProblem, setGeoProblem] = useState<string | null>(null);
  const cur = useCurrentTime(ws);
  // t182u: a clock-in uses a shift only inside its early window; otherwise it's unscheduled at a building you pick
  // (only one possible: used automatically; else nearest when location is already allowed, else home).
  const shiftNow = cur.data?.clock_shift_id ? cur.data.next_shift?.id === cur.data.clock_shift_id ? cur.data.next_shift : null : null;
  const candidates = cur.data?.clock_locations ?? [];
  const unscheduled = Boolean(cur.data && !cur.data.entry && !cur.data.clock_shift_id);
  const [here, setHere] = useState<GeoReading | null>(null);
  useEffect(() => {
    if (!unscheduled || candidates.length < 2 || !candidates.some(c => c.lat != null)) return;
    let live = true;
    void readPositionIfAllowed().then(r => { if (live) setHere(r); });
    return () => { live = false; };
  }, [unscheduled, candidates.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const chosen = optionalId(locationId) ?? pickClockLocation(candidates, { home: cur.data?.home_location_id, position: here }) ?? undefined;
  const targetLoc = cur.data?.entry?.location_id ?? shiftNow?.location_id ?? chosen;
  const preview = useClockPreview(ws, targetLoc, Boolean(cur.data));
  const fence = preview.data?.geofence ?? null;
  /** A reading only when this building checks location. */
  let geoFailure: GeoFailure | null = null;
  const position = async (): Promise<GeoReading | null> => {
    if (!fence) return null;
    const r = await readPosition();
    if (!r.reading && r.failure) { setGeoProblem(GEO_FAILURE_TEXT[r.failure]); geoFailure = r.failure; }
    return r.reading;
  };
  // t187u: punches waiting to send (no signal) show straight away, marked as waiting.
  const pendingOps = usePendingClock(ws);
  const view = useMemo(() => applyPendingClock({ entry: cur.data?.entry ?? null }, pendingOps), [cur.data?.entry, pendingOps]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 15_000); return () => clearInterval(t); }, []);

  const run = useMutation({
    mutationFn: async (action: ClockAction) => {
      const device_time = new Date().toISOString();
      geoFailure = null;
      const key = 'collab.clockin.event';
      const id = action === 'in' ? sessionStorage.getItem(key) ?? crypto.randomUUID() : crypto.randomUUID();
      if (action === 'in') sessionStorage.setItem(key, id);
      if (action === 'in' || action === 'out') setGeoProblem(null);
      const geo = action === 'in' || action === 'out' ? await position() : null;
      const shiftId = optionalId(cur.data?.clock_shift_id);
      const r = await submitClock(ws!, action, {
        client_event_id: id, device_time, geo, geo_failure: geo ? null : geoFailure,
        ...(CLOCK_ACTION_OP[action] === 'clock.in' ? { shift_id: shiftId ?? null, location_id: shiftId ? null : optionalId(chosen) ?? null } : { clock_in_event_id: pendingClockIn(ws!)?.payload.client_event_id ?? null }),
      });
      if (action === 'in') sessionStorage.removeItem(key);
      return r;
    },
    onSuccess: async (res, action) => {
      if (res.queued) { toast('Saved · sends when there\'s signal', 'info'); return; }
      const r = res.entry;
      const g = action === 'in' ? r.clock_in_geo : action === 'out' ? r.clock_out_geo : null;
      if (g === 'outside' || g === 'unknown') toast(g === 'outside' ? 'You\'re away from the building - your manager will see this on your timesheet' : 'We couldn\'t confirm your location - your manager will see this', 'info');
      // Backlog #19: show the last shift's handovers straight after clocking in.
      if (action === 'in') {
        const pending = await api.wf.pendingHandovers(ws!).catch(() => []);
        if (pending.length) setShowPending(true);
      } toast(action === 'in' ? 'Clocked in' : action === 'out' ? 'Clocked out - timesheet submitted' : action === 'break' ? 'Break started' : 'Break ended', 'success'); },
    onError: e => {
      // Refused by the building's geofence: explain in place rather than a toast.
      if (e instanceof ApiError && (e.body as { code?: string } | null)?.code === 'outside_geofence') { setGeoProblem(errorMessage(e)); return; }
      toast(errorMessage(e), 'error');
    },
  });

  // t692u: handovers need a template; no button when none applies at this building.
  const canHandover = useHandoverTemplatesAvailable(view.entry?.location_id, Boolean(view.entry)) === true;
  if (cur.isLoading) return <Card title="Time clock"><Spinner /></Card>;
  const entry = view.entry;
  const waiting = view.pendingOps > 0;
  const next = cur.data?.next_shift;
  const onBreak = entry?.breaks.some(b => !b.ended_at);
  const worked = entry ? entryMinutes(entry, now) : null;

  return (
    <Card title={<span className="flex items-center gap-2"><Timer className="h-4 w-4 text-accent-fg" />Time clock</span>}>
      {entry ? (
        <div className="space-y-3">
          <div className="flex items-baseline gap-3">
            <Link to="/workforce/timesheets" className="text-3xl font-semibold tabular-nums decoration-2 underline-offset-4 hover:underline" aria-label={`View my timesheet: ${minutes(worked?.paid_minutes ?? 0)} worked so far`} data-testid="home-stat" data-stat="clock-worked">{minutes(worked?.paid_minutes ?? 0)}</Link>
            <span className="text-sm text-slate-500">since {clock(dt(entry.clock_in_at))} at {locationName(entry.location_id)}</span>
            <GeoBadge geo={entry.clock_in_geo} distance={entry.clock_in_distance_m} />
            {waiting && <WaitingMarker />}
          </div>
          {onBreak && <div className="rounded-md bg-orange-50 px-3 py-1.5 text-sm text-orange-800 dark:bg-orange-900/40 dark:text-orange-200">On break</div>}
          <div className="flex gap-2">
            {onBreak
              ? <Button variant="secondary" onClick={() => run.mutate('resume')} loading={run.isPending}><Coffee className="h-4 w-4" />End break</Button>
              : <Button variant="secondary" onClick={() => run.mutate('break')} loading={run.isPending}><Coffee className="h-4 w-4" />Start break</Button>}
            <Button variant="danger" onClick={() => run.mutate('out')} loading={run.isPending}><LogOut className="h-4 w-4" />Clock out</Button>
            {canHandover && <Button variant="ghost" onClick={() => setWriting(true)}><ClipboardSignature className="h-4 w-4" />Write handover</Button>}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {next && <div className="text-sm"><span className="text-slate-500">{shiftNow ? 'Shift: ' : 'Next shift: '}</span><span className="font-medium">{dt(next.starts_at).toFormat('ccc d LLL')} {shiftTimes(next)}</span> at {locationName(next.location_id)}</div>}
          {unscheduled && candidates.length > 0 && (
            <div className="flex min-w-0 items-center gap-2 text-sm" data-testid="clock-building">
              <MapPin className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
              <span className="shrink-0 text-slate-500">No shift now · at</span>
              {candidates.length === 1
                ? <span className="font-medium">{candidates[0].name}</span>
                : <select aria-label="Building" className="min-w-0 flex-1 rounded-md border border-slate-300 bg-transparent px-2 py-1 text-sm dark:border-slate-700" value={chosen ?? ''} onChange={e => setLocationId(e.target.value)}>
                    {candidates.map(l => <option key={l.id} value={l.id}>{l.name}{l.id === cur.data?.home_location_id ? ' (home)' : ''}</option>)}
                  </select>}
            </div>
          )}
          {waiting && <WaitingMarker />}
          <Button variant="primary" onClick={() => run.mutate('in')} loading={run.isPending}><LogIn className="h-4 w-4" />Clock in</Button>
        </div>
      )}
      {/* t161: no on-site explanation here; the privacy wording lives in the Help Centre (Clock in and out). */}
      {geoProblem && (
        <div role="alert" className="mt-3 flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-900 dark:border-orange-900 dark:bg-orange-950 dark:text-orange-200" data-testid="geofence-problem">
          <MapPinOff className="mt-0.5 h-4 w-4 shrink-0" /><span>{geoProblem}</span>
        </div>
      )}
      <PendingHandoversModal open={showPending} onClose={() => setShowPending(false)} />
      <HandoverFormModal open={writing} onClose={() => setWriting(false)} defaults={entry ? { location_id: entry.location_id, shift_id: entry.shift_id ?? null } : undefined} />
    </Card>
  );
}
